/**
 * useDividends — Dividend tracking & income forecasting store (Zustand).
 */
import { create } from 'zustand'
import {
  getDividendPortfolio,
  getDividendCalendar,
  getDividendHistory,
  getDividendAristocrats,
  getDividendTopYield,
} from '@/lib/api'
import type {
  DividendPortfolioResponse,
  DividendCalendarResponse,
  DividendHistoryResponse,
  DividendAristocratsResponse,
  TopYieldResponse,
} from '@/lib/types'

interface DividendsState {
  // Portfolio income
  portfolio: DividendPortfolioResponse | null
  portfolioLoading: boolean

  // Calendar
  calendar: DividendCalendarResponse | null
  calendarLoading: boolean

  // History (per ticker)
  history: DividendHistoryResponse | null
  historyTicker: string | null
  historyLoading: boolean

  // Aristocrats
  aristocrats: DividendAristocratsResponse | null
  aristocratsLoading: boolean

  // Top Yield
  topYield: TopYieldResponse | null
  topYieldLoading: boolean

  error: string | null

  fetchPortfolio: () => Promise<void>
  fetchCalendar: (days?: number) => Promise<void>
  fetchHistory: (ticker: string) => Promise<void>
  fetchAristocrats: () => Promise<void>
  fetchTopYield: (limit?: number) => Promise<void>
}

export const useDividends = create<DividendsState>()((set) => ({
  portfolio: null,
  portfolioLoading: false,
  calendar: null,
  calendarLoading: false,
  history: null,
  historyTicker: null,
  historyLoading: false,
  aristocrats: null,
  aristocratsLoading: false,
  topYield: null,
  topYieldLoading: false,
  error: null,

  fetchPortfolio: async () => {
    set({ portfolioLoading: true, error: null })
    try {
      // Request confidence data so the dividends page can show the
      // reliable/speculative income split and per-holding badges.
      const portfolio = await getDividendPortfolio(true)
      set({ portfolio, portfolioLoading: false })
    } catch (e) {
      set({ error: String(e), portfolioLoading: false })
    }
  },

  fetchCalendar: async (days = 90) => {
    set({ calendarLoading: true, error: null })
    try {
      const calendar = await getDividendCalendar(days)
      set({ calendar, calendarLoading: false })
    } catch (e) {
      set({ error: String(e), calendarLoading: false })
    }
  },

  fetchHistory: async (ticker: string) => {
    set({ historyLoading: true, historyTicker: ticker, error: null })
    try {
      const history = await getDividendHistory(ticker)
      set({ history, historyLoading: false })
    } catch (e) {
      set({ error: String(e), historyLoading: false })
    }
  },

  fetchAristocrats: async () => {
    set({ aristocratsLoading: true, error: null })
    try {
      const aristocrats = await getDividendAristocrats()
      set({ aristocrats, aristocratsLoading: false })
    } catch (e) {
      set({ error: String(e), aristocratsLoading: false })
    }
  },

  fetchTopYield: async (limit = 20) => {
    set({ topYieldLoading: true, error: null })
    try {
      const topYield = await getDividendTopYield(limit)
      set({ topYield, topYieldLoading: false })
    } catch (e) {
      set({ error: String(e), topYieldLoading: false })
    }
  },
}))
